/**
 * SetupAssistantButton — One-click auto-configuration for MCP templates.
 *
 * Replaces the "Create from Template" button when a template has a
 * setup_assistant with mode != 'manual'. Handles:
 *   - platform_oauth2: Open popup → OAuth consent → auto-create server
 *   - oauth2_redirect: Same popup flow with pre-config fields
 *   - guided_wizard: (future) multi-step wizard
 *   - manual: Falls through to standard "Create from Template" flow
 *
 * Sprint: 63, Stream B, Tasks: B1 + B2
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Zap, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui';
import { useSetupInitiate, useSetupCallback } from '@/features/mcp';
import type { SetupAssistantDisplay } from '@/features/mcp/setup-types';
import { useI18n } from '@/i18n';

// =============================================================================
// Props
// =============================================================================

interface SetupAssistantButtonProps {
  templateSlug: string;
  mode: string;
  display: SetupAssistantDisplay | null;
}

// =============================================================================
// Constants
// =============================================================================

const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 700;

// =============================================================================
// Component
// =============================================================================

export function SetupAssistantButton({
  templateSlug,
  mode,
  display,
}: SetupAssistantButtonProps) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const initiate = useSetupInitiate();
  const callbackMutation = useSetupCallback();

  const [connecting, setConnecting] = useState(false);
  const [success, setSuccess] = useState(false);
  const popupRef = useRef<Window | null>(null);
  const stateRef = useRef<string>('');

  // ---------------------------------------------------------------------------
  // Handle postMessage from the OAuth popup
  // ---------------------------------------------------------------------------
  const handleMessage = useCallback(
    async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'oauth-callback') return;

      const { code, state, error } = event.data;

      if (error) {
        toast.error(t('admin.setup.consentDenied'));
        setConnecting(false);
        return;
      }

      // Verify CSRF state
      if (state !== stateRef.current) {
        toast.error(t('admin.setup.stateMismatch'));
        setConnecting(false);
        return;
      }

      try {
        const result = await callbackMutation.mutateAsync({
          flowType: mode,
          data: { code, state },
        });

        setSuccess(true);
        toast.success(
          t('admin.setup.serverCreated', { name: result.server_name }),
        );

        // Navigate to the newly created server after a brief delay
        setTimeout(() => {
          navigate(`/integrations/servers/${result.server_id}`);
        }, 1500);
      } catch {
        toast.error(t('admin.setup.callbackFailed'));
      } finally {
        setConnecting(false);
      }
    },
    [callbackMutation, mode, navigate, t],
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  // ---------------------------------------------------------------------------
  // Start the OAuth popup flow
  // ---------------------------------------------------------------------------
  const handleConnect = async () => {
    if (connecting || success) return;
    setConnecting(true);

    try {
      const result = await initiate.mutateAsync({ slug: templateSlug });

      if (!result.authorization_url || !result.state) {
        toast.error(t('admin.setup.initFailed'));
        setConnecting(false);
        return;
      }

      stateRef.current = result.state;

      // Center the popup on screen
      const left = window.screenX + (window.outerWidth - POPUP_WIDTH) / 2;
      const top = window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2;

      popupRef.current = window.open(
        result.authorization_url,
        'mcp-setup-oauth',
        `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},popup=yes`,
      );

      if (!popupRef.current) {
        toast.error(t('admin.setup.popupBlocked'));
        setConnecting(false);
      }
    } catch {
      toast.error(t('admin.setup.initFailed'));
      setConnecting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const buttonLabel = display?.connect_button_label
    ?? t('admin.setup.connectDefault');

  if (success) {
    return (
      <Button disabled className="gap-2">
        <CheckCircle className="w-4 h-4" />
        {t('admin.setup.created')}
      </Button>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <Button
        onClick={handleConnect}
        disabled={connecting}
        className="gap-2"
      >
        {connecting ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Zap className="w-4 h-4" />
        )}
        {connecting ? t('admin.setup.connecting') : buttonLabel}
      </Button>

      {display?.permissions_summary && display.permissions_summary.length > 0 && (
        <p className="text-[11px] text-muted-foreground leading-tight max-w-[260px]">
          {t('admin.setup.permissionsNote', {
            count: display.permissions_summary.length,
          })}
        </p>
      )}
    </div>
  );
}
